import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import Hls from 'hls.js';
import i18n from '@/i18n/config';
import {
  readVideoPreferences,
  writeVideoPreferences,
} from '@/utils/videoPreferences';

export type HlsPlaybackEngine = 'idle' | 'native' | 'hls.js' | 'unsupported';
export type HlsPlaybackStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface HlsQualityLevel {
  index: number;
  height: number;
  bitrate: number;
}

export interface HlsPlaybackState {
  engine: HlsPlaybackEngine;
  status: HlsPlaybackStatus;
  error: string | null;
  levels: HlsQualityLevel[];
  activeHeight: number | null;
  activeBitrate: number | null;
  isAutoQuality: boolean;
}

const INITIAL_STATE: HlsPlaybackState = {
  engine: 'idle',
  status: 'idle',
  error: null,
  levels: [],
  activeHeight: null,
  activeBitrate: null,
  isAutoQuality: true,
};

export const supportsNativeHls = (video: HTMLVideoElement): boolean =>
  Boolean(
    video.canPlayType('application/vnd.apple.mpegurl') ||
      video.canPlayType('application/x-mpegURL')
  );

export const useHlsPlayer = (
  videoRef: RefObject<HTMLVideoElement>,
  sourceUrl: string | null
) => {
  const hlsRef = useRef<Hls | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [state, setState] = useState<HlsPlaybackState>(INITIAL_STATE);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !sourceUrl) {
      setState(INITIAL_STATE);
      return;
    }

    let disposed = false;
    const updateState = (update: Partial<HlsPlaybackState>) => {
      if (!disposed) {
        setState((current) => ({ ...current, ...update }));
      }
    };

    if (!Hls.isSupported() && supportsNativeHls(video)) {
      const handleReady = () => updateState({ status: 'ready', error: null });
      const handleError = () =>
        updateState({
          status: 'error',
          error: i18n.t('pages:sessions.videoPlayback.hlsLoadFailed'),
        });

      setState({
        ...INITIAL_STATE,
        engine: 'native',
        status: 'loading',
      });
      video.addEventListener('loadedmetadata', handleReady);
      video.addEventListener('error', handleError);
      video.src = sourceUrl;
      video.load();

      return () => {
        disposed = true;
        video.removeEventListener('loadedmetadata', handleReady);
        video.removeEventListener('error', handleError);
        // React may already have committed a replacement MP4 source before
        // this passive cleanup runs. Only clear the native HLS source when it
        // is still the source owned by this effect.
        if (video.getAttribute('src') === sourceUrl) {
          video.removeAttribute('src');
          video.load();
        }
      };
    }

    if (!Hls.isSupported()) {
      setState({
        ...INITIAL_STATE,
        engine: 'unsupported',
        status: 'error',
        error: i18n.t('pages:sessions.videoPlayback.hlsUnsupportedBrowser'),
      });
      return;
    }

    const hls = new Hls();
    hlsRef.current = hls;
    setState({
      ...INITIAL_STATE,
      engine: 'hls.js',
      status: 'loading',
    });

    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      hls.loadSource(sourceUrl);
    });
    hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
      const levels = data.levels
        .map((level, index) => ({
          index,
          height: level.height,
          bitrate: level.bitrate,
        }))
        .filter((level) => level.height > 0)
        // Highest first, so consumers render a menu in the order viewers
        // expect without each re-sorting. `index` stays the hls.js level
        // index, which is what currentLevel is addressed by.
        .sort((left, right) => right.height - left.height);

      // Re-apply the viewer's pinned rendition. hls.js is rebuilt from
      // scratch whenever sourceUrl changes — every fragment advance, and every
      // playback-token refresh — so without this the choice would silently
      // fall back to adaptive partway through a multi-part recording.
      const pinnedHeight = readVideoPreferences().qualityHeight;
      const pinnedLevel =
        pinnedHeight === null
          ? undefined
          : levels.find((level) => level.height === pinnedHeight);
      if (pinnedLevel) {
        hls.currentLevel = pinnedLevel.index;
      }

      updateState({
        status: 'ready',
        error: null,
        levels,
        // A stale preference (e.g. 1080p saved before a recording that only
        // has 720p/360p) falls back to adaptive rather than stranding the
        // viewer on a rendition that does not exist here.
        isAutoQuality: !pinnedLevel,
        ...(pinnedLevel
          ? { activeHeight: pinnedLevel.height, activeBitrate: pinnedLevel.bitrate }
          : {}),
      });
    });
    hls.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
      const level = hls.levels[data.level];
      updateState({
        activeHeight: level?.height ?? null,
        activeBitrate: level?.bitrate ?? null,
        isAutoQuality: hls.autoLevelEnabled,
      });
    });
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal) {
        return;
      }
      updateState({
        status: 'error',
        error: i18n.t('pages:sessions.videoPlayback.hlsPlaybackFailed', { details: data.details }),
      });
    });
    hls.attachMedia(video);

    return () => {
      disposed = true;
      hlsRef.current = null;
      // hls.js detaches its MediaSource only when it still owns the element.
      // Do not clear the element afterwards: React may already have assigned
      // the MP4 source for the next render.
      hls.destroy();
    };
  }, [reloadKey, sourceUrl, videoRef]);

  const selectQuality = useCallback((height: number | null) => {
    const hls = hlsRef.current;
    // Persisted even when no hls.js instance is attached yet, so the choice
    // still applies to the next manifest this player parses.
    writeVideoPreferences({ qualityHeight: height });
    if (!hls) {
      return;
    }

    if (height === null) {
      hls.currentLevel = -1;
      setState((current) => ({ ...current, isAutoQuality: true }));
      return;
    }

    const levelIndex = hls.levels.findIndex((level) => level.height === height);
    if (levelIndex >= 0) {
      hls.currentLevel = levelIndex;
      setState((current) => ({
        ...current,
        activeHeight: height,
        activeBitrate:
          hls.levels.find((level) => level.height === height)?.bitrate ?? null,
        isAutoQuality: false,
      }));
    }
  }, []);

  const retry = useCallback(() => {
    setReloadKey((current) => current + 1);
  }, []);

  return { ...state, selectQuality, retry };
};
